/**
 * Small accessible tooltip for the Hubs shell (rail labels when collapsed, item
 * descriptions in fly-outs). Shows on hover (after a short delay) and at once on
 * keyboard focus; hides on leave / blur / Escape. Rendered in a portal with fixed
 * positioning so the rail's and fly-out's scroll containers don't clip it. The
 * trigger gets `aria-describedby` while it's open.
 */
import React, { cloneElement, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DWA } from '../theme/dwaTokens';

type Placement = 'right' | 'bottom';

interface TooltipProps {
  content: React.ReactNode;
  /** One element that accepts mouse/focus handlers and a ref (a link, button, span with tabIndex). */
  children: React.ReactElement;
  placement?: Placement;
  /** Hover delay in ms (focus shows immediately). */
  delay?: number;
  disabled?: boolean;
}

type Handler = (e: React.SyntheticEvent) => void;

export const Tooltip: React.FC<TooltipProps> = ({ content, children, placement = 'right', delay = 350, disabled }) => {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const clear = () => window.clearTimeout(timer.current);
  const show = useCallback((now: boolean) => {
    window.clearTimeout(timer.current);
    if (now) setOpen(true);
    else timer.current = window.setTimeout(() => setOpen(true), delay);
  }, [delay]);
  const hide = useCallback(() => {
    window.clearTimeout(timer.current);
    setOpen(false);
  }, []);

  useEffect(() => clear, []);
  useEffect(() => {
    if (disabled) hide();
  }, [disabled, hide]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const tip = tipRef.current?.getBoundingClientRect();
    const w = tip?.width ?? 240;
    const h = tip?.height ?? 40;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top: number;
    let left: number;
    if (placement === 'right' && r.right + 10 + w <= vw - 8) {
      left = r.right + 10;
      top = r.top + r.height / 2 - h / 2;
    } else {
      left = r.left;
      top = r.bottom + 6;
    }
    left = Math.max(8, Math.min(left, vw - w - 8));
    top = Math.max(8, Math.min(top, vh - h - 8));
    setPos({ top, left });
  }, [open, placement, content]);

  const child = children as React.ReactElement<Record<string, unknown>>;
  const chain = (name: string, fn: Handler) => (e: React.SyntheticEvent) => {
    const own = child.props[name] as Handler | undefined;
    own?.(e);
    fn(e);
  };

  const trigger = cloneElement(child, {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      const { ref } = child as unknown as { ref?: React.Ref<HTMLElement> };
      if (typeof ref === 'function') ref(node);
      else if (ref && typeof ref === 'object') (ref as React.MutableRefObject<HTMLElement | null>).current = node;
    },
    onMouseEnter: chain('onMouseEnter', () => !disabled && show(false)),
    onMouseLeave: chain('onMouseLeave', hide),
    onFocus: chain('onFocus', () => !disabled && show(true)),
    onBlur: chain('onBlur', hide),
    'aria-describedby': open ? id : (child.props['aria-describedby'] as string | undefined),
  });

  return (
    <>
      {trigger}
      {open &&
        !disabled &&
        createPortal(
          <div
            ref={tipRef}
            id={id}
            role="tooltip"
            style={{
              position: 'fixed',
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              zIndex: 2000,
              maxWidth: 260,
              background: '#0d0d0d',
              color: DWA.onDark,
              border: `1px solid ${DWA.railLine}`,
              boxShadow: '0 8px 24px -8px rgba(0,0,0,0.6)',
              borderRadius: DWA.radius,
              padding: '0.5rem 0.65rem',
              fontSize: '0.78rem',
              lineHeight: 1.45,
              fontFamily: DWA.font,
              pointerEvents: 'none',
            }}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
};
